import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Mail, Plus, X } from "lucide-react";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function RecipientDialog({ open, onOpenChange, title, description, job, onSend, withNote = false }) {
  const [note, setNote] = useState("");
  const contacts = (job.client?.contacts || []).filter((c) => c.email);
  const defaultId = job.contact?.id || contacts.find((c) => c.primary)?.id || contacts[0]?.id;
  const [picked, setPicked] = useState(new Set(defaultId ? [defaultId] : []));
  const [extras, setExtras] = useState([]);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const total = picked.size + extras.length;
  const allPicked = contacts.length > 0 && picked.size === contacts.length;

  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const addCustom = () => {
    const em = custom.trim().toLowerCase();
    if (!EMAIL_RE.test(em) || extras.includes(em)) return;
    setExtras((x) => [...x, em]); setCustom("");
  };
  const send = async () => {
    setBusy(true);
    try { await onSend({ contact_ids: [...picked], emails: extras, ...(withNote ? { note: note.trim() } : {}) }); onOpenChange(false); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="recipient-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
        <div className="space-y-1.5">
          {contacts.length > 0 && (
            <div className="flex items-center justify-between mb-1">
              <div className="sm-label">{job.client?.firm_name} contacts</div>
              <button type="button" className="sm-link text-xs" data-testid="recipient-select-all" onClick={() => setPicked(allPicked ? new Set() : new Set(contacts.map((c) => c.id)))}>{allPicked ? "Clear all" : "Select all"}</button>
            </div>
          )}
          {contacts.length === 0 && <div className="text-xs text-slate-500 pb-1">No contacts with an email on file for this company.</div>}
          {contacts.map((c) => (
            <label key={c.id} className={`flex items-center gap-3 rounded-md border p-2.5 cursor-pointer ${picked.has(c.id) ? "border-[#2d7bd0] bg-[#2d7bd0]/5" : "border-slate-200"}`} data-testid={`recipient-option-${c.id}`}>
              <Checkbox checked={picked.has(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`recipient-check-${c.id}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{c.name}{c.title ? <span className="text-slate-400 font-normal"> · {c.title}</span> : null}{c.primary && <span className="ml-2 text-[10px] uppercase text-emerald-700 font-semibold">Primary</span>}</div>
                <div className="text-xs text-slate-500 truncate">{c.email}</div>
              </div>
            </label>
          ))}
          {extras.map((em) => (
            <div key={em} className="flex items-center gap-3 rounded-md border border-[#2d7bd0] bg-[#2d7bd0]/5 p-2.5" data-testid={`recipient-extra-${em}`}>
              <Mail className="h-4 w-4 text-slate-400" /><div className="text-sm flex-1 truncate">{em}</div>
              <button type="button" onClick={() => setExtras((x) => x.filter((e) => e !== em))} className="text-slate-400 hover:text-rose-600" data-testid={`recipient-extra-remove-${em}`}><X className="h-4 w-4" /></button>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <Input type="email" placeholder="Add another email address…" value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} className="h-8" data-testid="recipient-custom-email" />
            <Button type="button" size="sm" variant="outline" className="h-8" disabled={!EMAIL_RE.test(custom.trim())} onClick={addCustom} data-testid="recipient-custom-add"><Plus className="h-3.5 w-3.5" /></Button>
          </div>
          {withNote && <textarea className="w-full mt-2 rounded-md border border-slate-200 p-2 text-sm min-h-[64px]" placeholder="Optional note to include in the email…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="recipient-note" />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="recipient-cancel">Cancel</Button>
          <Button className="sm-green" disabled={total === 0 || busy} onClick={send} data-testid="recipient-send"><Mail className="h-3.5 w-3.5 mr-1.5" />{busy ? "Sending…" : `Send${total > 1 ? ` to ${total}` : ""}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
